import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone, MessageCircle, UserX, AlertTriangle, Search,
  ChevronLeft, ChevronRight, Calendar, RefreshCw
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, telLink, waLink, todayIST, fmtDate, initials, fillTemplate } from "@/lib/format";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AbsentPatients() {
  const navigate = useNavigate();
  const today = todayIST();
  const [items, setItems] = useState([]);
  const [tpl, setTpl] = useState("");
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, t] = await Promise.all([
        api.get("/attendance/absent"),
        api.get("/whatsapp-templates"),
      ]);
      setItems(a.data.items || []);
      setTpl((t.data.items.find((x) => x.key === "absent") || {}).message || "");
    } catch (err) {
      setError(apiError(err, "Failed to load absent patients list"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = items;
    if (sourceFilter !== "all") {
      list = list.filter((i) => (i.source || "AUTOMATIC").toUpperCase() === sourceFilter.toUpperCase());
    }
    const q = search.toLowerCase().trim();
    if (q) {
      list = list.filter(
        (i) =>
          (i.name || "").toLowerCase().includes(q) ||
          (i.phone || "").includes(q) ||
          (i.code || "").toLowerCase().includes(q) ||
          (i.problem || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, sourceFilter, search]);

  if (loading) return <Loading label="Loading absent patients..." />;

  if (error) {
    return (
      <div className="p-8 text-center bg-red-50 rounded-xl border border-red-200 my-6">
        <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-2" />
        <h3 className="font-display text-lg font-bold text-red-800">Unable to load Absent Patients</h3>
        <p className="text-sm text-red-600 mb-4">{error}</p>
        <Button onClick={load} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Absent Patients"
        subtitle={`${items.length} patient(s) needing clinical follow-up`}
        testid="absent-page"
      >
        <Button variant="outline" size="sm" onClick={load} title="Refresh absent list">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </PageHeader>

      {/* Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search absent patients by name, phone, code..."
            className="pl-9"
          />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="MANUAL">Manual Absent</SelectItem>
            <SelectItem value="AUTOMATIC">Automatic Absent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={UserX}
          title="No absent patients found"
          subtitle="Patients who missed their scheduled sessions will appear here with consecutive absence counts and follow-up tools."
        />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {filtered.map((a) => (
            <Card
              key={a.patient_id}
              data-testid={`absent-card-${a.code}`}
              className="p-4 hover:shadow-sm transition-all border-slate-200 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start gap-3">
                  <Avatar
                    className="h-11 w-11 cursor-pointer ring-2 ring-rose-500/20"
                    onClick={() => navigate(`/patients/${a.patient_id}`)}
                  >
                    <AvatarFallback className="bg-red-50 text-bk-danger font-semibold text-sm">
                      {initials(a.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p
                        className="font-semibold text-slate-800 truncate cursor-pointer hover:text-bk-blue"
                        onClick={() => navigate(`/patients/${a.patient_id}`)}
                      >
                        {a.name}
                      </p>
                      <StatusBadge status="absent" source={a.source} />
                    </div>
                    <p className="text-xs text-slate-500 truncate">
                      {a.problem} · <span className="font-mono">{a.code}</span>
                    </p>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap text-xs">
                      {a.last_attended ? (
                        <Badge variant="outline" className="text-[10px] bg-slate-50 border-slate-200">
                          Last visit {fmtDate(a.last_attended)}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] bg-slate-50 border-slate-200 text-slate-400">
                          No previous visits
                        </Badge>
                      )}

                      {a.consecutive_absences >= 7 ? (
                        <Badge className="text-[10px] bg-bk-danger text-white">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          High Priority · {a.consecutive_absences}d
                        </Badge>
                      ) : a.consecutive_absences >= 3 ? (
                        <Badge className="text-[10px] bg-amber-500 text-white">
                          Follow-up · {a.consecutive_absences}d
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">
                          {a.consecutive_absences}d absent
                        </Badge>
                      )}

                      {a.follow_up_status && (
                        <Badge variant="outline" className="text-[10px] capitalize bg-blue-50 text-bk-blue border-blue-200">
                          {a.follow_up_status.replace(/_/g, " ")}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                <a href={telLink(a.phone)} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full h-8 text-xs text-bk-blue border-blue-200 hover:bg-blue-50">
                    <Phone className="h-3.5 w-3.5 mr-1" /> Call
                  </Button>
                </a>
                <a
                  href={waLink(
                    a.whatsapp || a.phone,
                    tpl ? fillTemplate(tpl, { name: a.name }) : `Namaste ${a.name} ji, aap aaj BK Spine Care clinic me nahi aa paye. Sab kushal hai na?`
                  )}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1"
                >
                  <Button variant="outline" size="sm" className="w-full h-8 text-xs text-bk-green border-emerald-200 hover:bg-emerald-50" data-testid={`wa-absent-${a.code}`}>
                    <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                  </Button>
                </a>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-8 text-xs"
                  onClick={() => navigate("/follow-ups")}
                >
                  Follow-ups
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="h-8 text-xs bg-slate-800 hover:bg-slate-900 px-3"
                  onClick={() => navigate(`/patients/${a.patient_id}`)}
                >
                  Profile
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

